import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useSaveFeedback } from '@features/feedback/api/saveFeedback.gql'

import { handlePhonePaste } from '@shared/__HACKS'
import Button from '@shared/ui/button'
import Checkbox from '@shared/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/ui/form'
import Input from '@shared/ui/input'
import MaskedInput from '@shared/ui/input-mask'
import PolicyLink from '@shared/ui/policy'
import Textarea from '@shared/ui/text-area'
import { PHONE_MASK_LENGTH } from '@shared/validation'

import styles from './styles.module.scss'

export default function FeedbackModal () {
  const { t: tc } = useTranslation('common')
  const { t: tv } = useTranslation('validation')
  const [ fetchSaveShopFeedback ] = useSaveFeedback()

  const REQUIRED_MESSAGE = tv('required')

  const formSchema = z.object({
    phone: z.string().min(PHONE_MASK_LENGTH, {
      message: tv('phone'),
    }),
    name: z.string().min(1, {
      message: REQUIRED_MESSAGE,
    }),
    email: z
      .string()
      .optional()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: tv('email'),
      }),
    comment: z.string(),
    policy: z.boolean().refine((val) => val === true, {
      message: REQUIRED_MESSAGE,
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: '',
      name: '',
      email: '',
      comment: '',
      policy: false,
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log(values)

    try {
      await fetchSaveShopFeedback({
        variables: {
          feedbackInfo: {
            brand_id: '1',
            order_id: null,
            shop_id: null,
            images: [],
            need_call: true,
            review: values.comment,
            general_rating: 5,
          },
        },
      })
    }
    catch (error) {
      console.log(error)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={tc('feedback.placeholder.name')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MaskedInput
                  {...field}
                  placeholder={tc('feedback.placeholder.phone')}
                  onPaste={handlePhonePaste}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={tc('feedback.placeholder.email')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={tc('feedback.placeholder.comment')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className={styles.bottom}>
          <div className={styles.group}>
            <Button size="large" type="submit">
              {tc('submit')}
            </Button>
            <Button size="large" theme="grey">
              {tc('cancel')}
            </Button>
          </div>

          <FormField
            control={form.control}
            name="policy"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    {...field}
                    label={<PolicyLink buttonText={tc('submit')} />}
                    value={String(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                    checkboxSize="extra_small"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
